import { useState, useEffect, useCallback } from 'react';
import SectionHeader from '../../components/admin/SectionHeader';
import DataTable from '../../components/admin/DataTable';
import Badge from '../../components/admin/Badge';
import AdminModal from '../../components/admin/AdminModal';

const SCOPE_HINT = {
  'servers:read': 'Read server inventory & capacity',
  'deploy:write': 'Report deployment results',
  'status:write': 'Update instance status',
  'access:write': 'Write access details',
  'alerts:write': 'Raise admin alerts',
  'notify:write': 'Send customer notifications (version updates)',
  'dns:write': 'Point instance domains at servers (Cloudflare)',
  '*': 'Full access (all scopes)',
};

function AdminApiKeys({ apiFetch, toast }) {
  const [keys, setKeys] = useState([]);
  const [allScopes, setAllScopes] = useState(['servers:read', 'deploy:write', 'status:write', 'access:write', 'alerts:write', '*']);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState(new Set(['servers:read', 'deploy:write', 'status:write', 'access:write', 'alerts:write', 'notify:write', 'dns:write']));
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/admin/api-keys');
      if (!res.ok) throw new Error();
      const data = await res.json();
      setKeys(data.keys || []);
      if (Array.isArray(data.scopes)) setAllScopes(data.scopes);
    } catch { toast.error('Error', 'Failed to load API keys'); }
    finally { setLoading(false); }
  }, [apiFetch, toast]);

  useEffect(() => { load(); }, [load]);

  const toggleScope = (s) => setScopes(prev => {
    const next = new Set(prev);
    if (next.has(s)) next.delete(s); else next.add(s);
    return next;
  });

  const create = async () => {
    if (!name.trim()) { toast.error('Error', 'Give the key a name'); return; }
    if (scopes.size === 0) { toast.error('Error', 'Select at least one scope'); return; }
    setCreating(true);
    try {
      const res = await apiFetch('/api/admin/api-keys', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), scopes: [...scopes] }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error('Error', data.error || 'Failed to create key'); return; }
      setNewKey(data.key);
      setShowCreate(false);
      setName('');
      load();
    } catch { toast.error('Error', 'Failed to create key'); }
    finally { setCreating(false); }
  };

  const revoke = async (row) => {
    if (!window.confirm(`Revoke API key "${row.name}"? Any automation using it will stop working immediately.`)) return;
    try {
      const res = await apiFetch(`/api/admin/api-keys/${row.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error();
      toast.success('Revoked', `Key "${row.name}" revoked`);
      load();
    } catch { toast.error('Error', 'Failed to revoke key'); }
  };

  const columns = [
    { key: 'name', header: 'Name', render: r => <span style={{ fontWeight: 600 }}>{r.name}</span> },
    { key: 'key_prefix', header: 'Key', render: r => <code style={{ fontSize: '12px', color: 'var(--admin-text-muted)' }}>{r.key_prefix}…</code> },
    { key: 'scopes', header: 'Scopes', render: r => (
      <span style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
        {(r.scopes || []).map(s => <Badge key={s} variant={s === '*' ? 'suspended' : 'info'}>{s}</Badge>)}
      </span>
    )},
    { key: 'created_by', header: 'Created by', cellStyle: { fontSize: '12px', color: 'var(--admin-text-muted)' } },
    { key: 'last_used_at', header: 'Last used', cellStyle: { fontSize: '12px', color: 'var(--admin-text-muted)' },
      render: r => r.last_used_at ? new Date(r.last_used_at).toLocaleString() : 'Never' },
    { key: 'status', header: 'Status', render: r => <Badge variant={r.revoked ? 'rejected' : 'active'}>{r.revoked ? 'Revoked' : 'Active'}</Badge> },
    { key: 'actions', header: '', render: r => r.revoked ? null : (
      <button className="admin-btn admin-btn--sm admin-btn--danger" onClick={() => revoke(r)}>Revoke</button>
    )},
  ];

  return (
    <>
      <SectionHeader
        title="API Keys"
        description="Machine credentials for automation (n8n) to call the control-plane API. Scoped, revocable, and audited."
        actions={<button className="admin-btn admin-btn--primary admin-btn--sm" onClick={() => { setScopes(new Set(['servers:read', 'deploy:write', 'status:write', 'access:write', 'alerts:write', 'notify:write', 'dns:write'])); setShowCreate(true); }}><i className="fa-solid fa-plus" style={{ marginRight: 4 }} />New key</button>}
      />

      <DataTable columns={columns} data={keys} loading={loading} emptyMessage="No API keys yet" emptyIcon="fa-solid fa-key" />

      {showCreate && (
        <AdminModal open title="New API key" onClose={() => setShowCreate(false)} width="520px">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>Name</label>
              <input className="admin-input" style={{ width: '100%' }} placeholder="e.g. n8n-deployment-orchestrator" value={name} onChange={e => setName(e.target.value)} autoFocus />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>Scopes (least privilege)</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {allScopes.map(s => (
                  <label key={s} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', cursor: 'pointer', opacity: (s !== '*' && scopes.has('*')) ? 0.5 : 1 }}>
                    <input type="checkbox" checked={scopes.has(s)} onChange={() => toggleScope(s)} />
                    <code style={{ fontSize: '12px' }}>{s}</code>
                    <span style={{ color: 'var(--admin-text-muted)' }}>— {SCOPE_HINT[s]}</span>
                  </label>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button className="admin-btn admin-btn--secondary admin-btn--sm" onClick={() => setShowCreate(false)}>Cancel</button>
              <button className="admin-btn admin-btn--primary admin-btn--sm" onClick={create} disabled={creating}>{creating ? 'Creating…' : 'Create key'}</button>
            </div>
          </div>
        </AdminModal>
      )}

      {newKey && (
        <AdminModal open title="Copy your API key now" onClose={() => setNewKey(null)} width="560px">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <p style={{ margin: 0, fontSize: '13px', color: 'var(--admin-text-secondary)' }}>
              This is the only time the full key is shown. Store it in your n8n credentials now — it cannot be retrieved later.
            </p>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
              <code style={{ flex: 1, padding: '10px 12px', background: 'var(--admin-bg)', border: '1px solid var(--admin-border)', borderRadius: '8px', fontSize: '12px', wordBreak: 'break-all' }}>{newKey}</code>
              <button className="admin-btn admin-btn--primary admin-btn--sm" onClick={() => { navigator.clipboard.writeText(newKey); toast.success('Copied', 'API key copied'); }}>Copy</button>
            </div>
            <div style={{ fontSize: '12px', color: 'var(--admin-text-muted)' }}>
              Send it as <code>Authorization: Bearer &lt;key&gt;</code> (or <code>X-Api-Key</code>) to <code>/api/v1/…</code>.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="admin-btn admin-btn--secondary admin-btn--sm" onClick={() => setNewKey(null)}>Done</button>
            </div>
          </div>
        </AdminModal>
      )}
    </>
  );
}

export default AdminApiKeys;
